import type {TicketListResponse} from './types';
import type {TicketFiltersValue} from 'common/ticket-filters/types';

import {FilterOutlined} from '@ant-design/icons';
import {Button, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useRouteBuildContext} from '@router';
import Empty from 'common/empty/Empty';
import TicketFilters from 'common/ticket-filters/TicketFilters';
import {createTicketFiltersFromQuery} from 'common/ticket-filters/utils';
import TicketingListItem from 'common/ticketing-list/TicketingListItem';
import {useCallback, useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {emptyTickets} from 'store/slices/ticket/slicer';
import {getTickets} from 'store/slices/ticket/thunk';
import {StringParam, useQueryParams, withDefault} from 'use-query-params';
import {getSelectedVendorFromStorage} from 'utils/Helper';

const TICKET_PAGE_SIZE = 20;

const Tickets: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {updateHeaderData} = useRouteBuildContext();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const {tickets, loading} = useAppSelector((state) => state.ticket);
  const [query, setQuery] = useQueryParams({
    order_code: withDefault(StringParam, null),
    type: withDefault(StringParam, null),
    state: withDefault(StringParam, null),
    from: withDefault(StringParam, null)
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const filters = useMemo<TicketFiltersValue>(() => createTicketFiltersFromQuery(query), [query]);

  const currentTickets = useMemo<TicketListResponse>(() => {
    return {
      tickets: Array.isArray(tickets?.tickets) ? tickets.tickets : [],
      total_items: typeof tickets?.total_items === 'number' ? tickets.total_items : 0
    };
  }, [tickets]);

  const loadTickets = useCallback(
    (requestedPage = 1, selectedFilters = filters) => {
      void dispatch(
        getTickets({
          vendor_id: vendorId,
          page_size: TICKET_PAGE_SIZE,
          page: requestedPage,
          ...(selectedFilters.order_code.trim()
            ? {order_code: selectedFilters.order_code.trim()}
            : {}),
          ...(selectedFilters.types.length > 0 ? {type: selectedFilters.types.join(',')} : {}),
          ...(selectedFilters.states.length > 0 ? {state: selectedFilters.states} : {})
        })
      );
    },
    [dispatch, filters, vendorId]
  );

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const activeFilterCount =
    Number(Boolean(filters.order_code.trim())) +
    Number(filters.types.length > 0) +
    Number(filters.states.length > 0);

  useEffect(() => {
    updateHeaderData('tickets', {
      icon: <FilterOutlined aria-hidden />,
      action: () => setIsFilterOpen(true),
      badgeNumber: activeFilterCount,
      iconButtonSize: 'sm'
    });
  }, [activeFilterCount, updateHeaderData]);

  const loadMoreTickets = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadTickets(nextPage);
  };

  const handleNewTicket = () => {
    dispatch(emptyTickets());
    const newTicketUrl = new URLSearchParams();
    if (query.type) {
      newTicketUrl.set('type', query.type);
    }
    if (query.order_code) {
      newTicketUrl.set('order_code', query.order_code);
    }
    if (query.from) {
      newTicketUrl.set('from', query.from);
    }
    navigate({
      pathname: '/new_ticket',
      search: newTicketUrl.toString()
    });
  };

  const isInitialLoading = Boolean(loading) && currentTickets.tickets.length === 0;
  const hasMoreTickets = currentTickets.tickets.length < currentTickets.total_items;

  const handleApplyFilters = (nextFilters: TicketFiltersValue) => {
    dispatch(emptyTickets());
    setPage(1);
    setIsFilterOpen(false);
    void setQuery(
      {
        order_code: nextFilters.order_code.trim() || null,
        type: nextFilters.types.length > 0 ? nextFilters.types.join(',') : null,
        state: nextFilters.states.length > 0 ? nextFilters.states.join(',') : null,
        from: query.from
      },
      'replace'
    );
  };

  return (
    <PageLayout
      contentId="tickets-list"
      footer={
        <Button fullWidth onClick={handleNewTicket}>
          گزارش درخواست جدید
        </Button>
      }
    >
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : currentTickets.tickets.length === 0 ? (
        <div className="flex min-h-60 flex-1 items-center justify-center px-10 text-center">
          <Empty>
            <p className="text-body-md-emphasis text-neutral-fg">اطلاعاتی برای نمایش وجود ندارد</p>
          </Empty>
        </div>
      ) : (
        <InfiniteScroll
          scrollableTarget="tickets-list"
          scrollThreshold={0.6}
          next={loadMoreTickets}
          hasMore={hasMoreTickets}
          dataLength={currentTickets.tickets.length}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
        >
          {currentTickets.tickets.map((ticket) => (
            <TicketingListItem
              key={ticket.id}
              item={ticket}
              onClick={() => {
                const ticketQuery = new URLSearchParams();
                if (query.order_code) ticketQuery.set('order_code', query.order_code);
                if (query.type) ticketQuery.set('type', query.type);
                if (query.state) ticketQuery.set('state', query.state);
                if (query.from) ticketQuery.set('from', query.from);

                navigate(
                  {
                    pathname: `/ticket/${ticket.id}`,
                    search: ticketQuery.toString()
                  },
                  {state: {orderCode: ticket.order_code ?? null}}
                );
              }}
            />
          ))}
        </InfiniteScroll>
      )}
      <TicketFilters
        show={isFilterOpen}
        filters={filters}
        onApply={handleApplyFilters}
        onClose={() => setIsFilterOpen(false)}
      />
    </PageLayout>
  );
};

export default Tickets;
